import {ChangeDetectionStrategy, Component, computed, effect, ElementRef, Injectable, input, output, signal, Signal, ViewChild} from "@angular/core";
import {AvtaleModell, AvtaleRessursClient, Avtalestatus, EnkelGruppeModell, GruppeModell, NyAvtaleOgGruppeSpesifikasjon, NyGruppeSpesifikasjon, Språk} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {concatMap, filter, firstValueFrom, Observable, take} from "rxjs";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {GruppeLogoOgFargeSkjemaKomponent} from "../gruppe-logo-og-farge-skjema/gruppe-logo-og-farge-skjema";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {InlineSkjemaHandlingerKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-handlinger/inline-skjema-handlinger";
import {InlineSkjemaKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema";
import {InlineSkjemaTittelKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-tittel/inline-skjema-tittel";
import {LokaleVelgerKomponent} from "web-ng2/delt/komponenter/skjema/lokale-velger/lokale-velger";
import {MatButton} from "@angular/material/button";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {NavigasjonforhindretFlashDirektiv} from "web-ng2/delt/direktiver/navigasjonforhindret-flash/navigasjonforhindret-flash";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {TidssoneVelgerKomponent} from "web-ng2/delt/komponenter/skjema/tidssone-velger/tidssone-velger";
import {TranslateModule} from "@ngx-translate/core";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {MatSelectModule} from "@angular/material/select";
import {AsyncPipe} from "@angular/common";
import {MatSelectTipsKomponent} from "web-ng2/delt/komponenter/mat-select-tips/mat-select-tips";
import {lagrerSignal} from "felles-ng2/tjenester/LagrerIndikator";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {GruppeDatatjeneste} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {harSammeId} from "felles-ng2/modell/modellHjelpere";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MatDialog} from "@angular/material/dialog";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {scrollTilTopp} from "web-ng2/delt/dom/scrollIntoView";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";
import {AvtaleGodtaLisensbetingelserDialogKomponent} from "web-ng2/moduler/avtale/modale/avtale-godta-lisensbetingelser-dialog/avtale-godta-lisensbetingelser-dialog";
import {aktiverFormKontroller, fjernAlleFeilFraFormGroup} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {filterNil} from "@ngneat/elf";
import {map} from "rxjs/operators";
import {GruppeRedigerKomponent} from "web-ng2/moduler/gruppe/komponenter/gruppe-rediger/gruppe-rediger";
import {MeldingTilBruker} from "felles/tjenester/MeldingTilBruker";
import {DataOgMeta} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {BrukerkontekstAdministrasjon} from "felles-ng2/tjenester/BrukerkontekstAdministrasjon";

const debug = initDebugger('GruppeNyKomponent');

export type SkjemaType = GruppeRedigerKomponent['skjema'];
export type SkjemaVerdier = SkjemaType['value'];

/**
 * Skjema for opprettelse av ny gruppe.
 */
@Component({
    selector: 'gruppe-ny',
    templateUrl: 'gruppe-ny.html',
    styleUrl: 'gruppe-ny.scss',
    host: {
        'data-testid': 'gruppe-ny',
        '[class.viser-fullt-skjema]': 'viserFulltSkjema()'
    },
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInput,
        MatButton,
        MatSlideToggleModule,
        TranslateModule,
        AsyncPipe,
        StandardValideringsfeilPipe,
        NavigasjonforhindretFlashDirektiv,
        InlineSkjemaKomponent,
        InlineSkjemaTittelKomponent,
        InlineSkjemaHandlingerKomponent,
        VisSpinnerDirektiv,
        InlineMeldingKomponent,
        LokaleVelgerKomponent,
        TidssoneVelgerKomponent,
        GruppeLogoOgFargeSkjemaKomponent,
        MatSelectTipsKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GruppeNyKomponent {
    static ajsSelector = 'gruppeNy';

    vedAvbryt = output<void>();
    vedOpprettet = output<GruppeModell>();

    gruppemaler!: Observable<EnkelGruppeModell[]>;
    avtaler!: Observable<AvtaleModell[]>;
    nyAvtaleVerdi: AvtaleModell;
    erValgt = harSammeId;
    visGodtaBetingelser = signal(false);
    viserFulltSkjema: Signal<boolean>;
    beregnOmFulltSkjemaSkalVises: Signal<boolean>;
    visFulltSkjemaHeleTiden = input<boolean>(false)
    lagrer = lagrerSignal()

    skjema = new FormGroup({
        tittel: new FormControl('', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng}),
        moetenavn: new FormControl('', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng}),
        mal: new FormControl<EnkelGruppeModell | null>(null, {nonNullable: true}), //mal er ikke påkrevet
        avtale: new FormControl<AvtaleModell | null>(null, {nonNullable: true, validators: Validators.required}),
        godtarAvtaleVilkaar: new FormControl(false, {nonNullable: true, validators: Validators.requiredTrue}), //kun påkrevet dersom mal = 'ny avtale'
        organisasjonsnavn: new FormControl('', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng}),
        spraak: new FormControl(Språk.norsk_bokmål, {nonNullable: true, validators: Validators.required}),
        tidssone: new FormControl('', {nonNullable: true, validators: Validators.required}),
        logoOgFarge: GruppeLogoOgFargeSkjemaKomponent.opprettSkjema(),
    });

    @ViewChild('skjemaelement') private skjemaelement!: ElementRef<HTMLFormElement>;
    private godtatteBetingelser = '';

    constructor(private avhengigheter: GruppeNyKomponentAvhengigheter,
                private navigasjonDeaktivering: NavigasjonDeaktivering,
                private meldingTilBruker: MeldingTilBruker,
                private oversettelser: Oversettelser,
                private matDialog: MatDialog) {
        this.nyAvtaleVerdi = {id: 'ny', navn: this.oversettelser.oversett(i18n('Ny avtale'))} as AvtaleModell;

        //kopier verdier fra mal når mal velges
        this.skjema.controls.mal.valueChanges.pipe(takeUntilDestroyed(), filterNil())
          .subscribe(avtale => this.vedMalValgt(avtale));

        //aktiver 'ny avtal' skjema dersom bruker velger dette
        this.skjema.controls.avtale.valueChanges.pipe(takeUntilDestroyed(), filterNil())
          .subscribe(avtale => this.vedAvtaleValgt(avtale));

        //vis fullt skjema når bruker fyller ut tittel-feltet
        this.beregnOmFulltSkjemaSkalVises = toSignal(this.skjema.valueChanges.pipe(takeUntilDestroyed(),
          map(skjema => this.skalFulltSkjemaVises(skjema))
        ), {initialValue: false});

        this.viserFulltSkjema = computed(() => {
            if(this.visFulltSkjemaHeleTiden()) return true;
            return this.beregnOmFulltSkjemaSkalVises();
        });

        //venter med å laste data til fullt skjema vises
        this.lastDataNårFulltSkjemaVises();

        //sørger for å scrolle slik at så mye som mulig av skjemaet vises når hele skjemaet aktiveres
        // + sørger for at man ikke kan navigere seg ut av skjema uforvarende
        effect(() => this.vedViserFulltSkjema(this.viserFulltSkjema()));
    }

    giFokusTilNyGruppeFelt() {
        const tittelFelt: HTMLInputElement = this.skjemaelement.nativeElement.querySelector('.kontainer input[name="tittel"]')!;
        tittelFelt.focus();
    }

    /** kalles nå bruker klikker på 'godta avtalevilkår'.
     * Merk at godtarAvtaleVilkaar allerede har blitt endret når denne metoden kalles, dvs
     * dersom switchen var 'false' før bruker klikket, vil verdien av godtarAvtaleVilkaar nå være 'true' */
    async bekreftAvtalebetingelser() {
        //dersom bruker endret fra 'godtatt' til 'ikke godtatt' gjør vi ingen ting
        if (!this.skjema.value.godtarAvtaleVilkaar) return;

        //bruker endrer til 'godtatt' - vi sørger da for å vise 'avtale vilkår' og setter
        //'godtarAvtaleVilkaar'=true dersom bruker godtar vilkår
        this.skjema.controls.godtarAvtaleVilkaar.setValue(false)
        const dialog = AvtaleGodtaLisensbetingelserDialogKomponent.vis(this.matDialog, this.skjema.value.spraak ?? Språk.norsk_bokmål);
        const respons = await firstValueFrom(dialog.afterClosed(), {defaultValue: AvtaleGodtaLisensbetingelserDialogKomponent.betingelserIkkeGodtatt});
        this.skjema.controls.godtarAvtaleVilkaar.setValue(respons!.godtatt);
        this.godtatteBetingelser = respons!.betingelser;
    }

    async opprett() {
        const nyGruppe = await this.lagrer.utfør(() => {
            return this.skjema.value.avtale === this.nyAvtaleVerdi ?
              this.avhengigheter.opprettMedNyAvtale(this.somMedNyAvtaleSpesifikasjon()) :
              this.avhengigheter.opprett(this.somSpesifikasjon());
        });
        this.resetSkjema();
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Ny gruppe opprettet')));
        this.vedOpprettet.emit(nyGruppe.data);
    }

    avbryt() {
        this.resetSkjema();
        this.vedAvbryt.emit();
    }

    kanLeggeTilNyGruppeIAvtale(avtale: AvtaleModell) {
        return avtale.avtalestatus  === Avtalestatus.aktiv || avtale.avtalestatus === Avtalestatus.prøveperiode;
    }

    //private hjelpemetoder

    private vedMalValgt(mal: EnkelGruppeModell){
        this.skjema.patchValue({
            moetenavn: mal.moetenavn,
            spraak: mal.oppsett.spraak,
            tidssone: mal.oppsett.tidssone
        });
    }

    /** Aktiver skjema for 'ny avtal' dersom bruker velger dette */
    private vedAvtaleValgt(avtale: AvtaleModell) {
        let erNyAvtale = avtale === this.nyAvtaleVerdi;
        //vis skjema for 'ny avtale' dersom bruker har valgt dette fra avtaler-dropdown
        this.visGodtaBetingelser.set(erNyAvtale);
        //dersom 'ny avtale' endrer vi hvilke felter som er aktivert
        aktiverFormKontroller(erNyAvtale, this.skjema.controls.godtarAvtaleVilkaar)
        // if (erNyAvtale) this.skjema.controls.godtarAvtaleVilkaar.enable();
        // else this.skjema.controls.godtarAvtaleVilkaar.removeValidators(Validators.required);
        //organisasjonsnavn populeres fra avtale, kan overstyres av bruker
        this.skjema.controls.organisasjonsnavn.setValue(erNyAvtale ? '' : avtale!.navn)
    }

    private skalFulltSkjemaVises(skjema: SkjemaVerdier): boolean {
        if (erIkkeTomStreng(skjema.tittel)) return true;
        if (erIkkeTomStreng(skjema.moetenavn)) return true;
        return false;
    }

    private lastDataNårFulltSkjemaVises() {
        let førsteGangFulltSkjemaVises = toObservable(this.viserFulltSkjema)
          .pipe(filter(viserFulltSkjema => viserFulltSkjema), take(1));
        this.gruppemaler = førsteGangFulltSkjemaVises.pipe(concatMap(() => this.avhengigheter.gruppemaler()));
        this.avtaler = førsteGangFulltSkjemaVises.pipe(concatMap(() => this.avhengigheter.avtaler()));
    }

    private vedViserFulltSkjema(viserFullt: boolean) {
        if (viserFullt) {
            // hivs ikke skjma er fullt tegnet ut nå denne kalles, så venter vi 200 ms før vi kaller den.
            if (!this.skjemaelement)
                window.setTimeout(() => scrollTilTopp(this.skjemaelement.nativeElement, 0), 200)
            else
                scrollTilTopp(this.skjemaelement.nativeElement, 200);
            this.navigasjonDeaktivering.deaktiverNavigasjon();
        } else {
            this.navigasjonDeaktivering.reaktiverNavigasjon();
        }
    }

    private somSpesifikasjon(): NyGruppeSpesifikasjon {
        let verdier = this.skjema.value;
        return {
            gruppenavn: verdier.tittel!,
            organisasjonsnavn: verdier.organisasjonsnavn!,
            moetenavn: verdier.moetenavn!,
            logoBase64: verdier.logoOgFarge!.logo ?? null,
            farge: verdier.logoOgFarge!.farge!,
            mal: verdier.mal?.id ?? null,
            avtale: verdier.avtale!.id,
            spraak: verdier.spraak!,
            tidssone: verdier.tidssone!,
        };
    }

    private somMedNyAvtaleSpesifikasjon(): NyAvtaleOgGruppeSpesifikasjon {
        let verdier = this.skjema.value;
        return {
            avtale: {
                navn: verdier.organisasjonsnavn!,
                lisensAkseptert: verdier.godtarAvtaleVilkaar!,
                aksepterteBetingelser: this.godtatteBetingelser
            },
            gruppe: this.somSpesifikasjon()
        };
    }

    private resetSkjema() {
        this.skjema.reset();
        this.skjema.markAsPristine();
        this.skjema.markAsUntouched();
        fjernAlleFeilFraFormGroup(this.skjema);
        this.navigasjonDeaktivering.reaktiverNavigasjon();
    }
}

@Injectable()
export class GruppeNyKomponentAvhengigheter {

    constructor(private gruppeDatatjeneste: GruppeDatatjeneste, private avtaleRessurs: AvtaleRessursClient, private kontekstAdministrasjon: BrukerkontekstAdministrasjon) {
    }

    gruppemaler(): Observable<EnkelGruppeModell[]> {
        return this.gruppeDatatjeneste.gruppemaler();
    }

    avtaler(): Observable<AvtaleModell[]> {
        return this.avtaleRessurs.finnAvtalerBrukerHarAktivtMedlemskapI().somObservable();
    }

    async opprett(spesifikasjon: NyGruppeSpesifikasjon): Promise<DataOgMeta<GruppeModell>> {
        var nyGruppe = await this.gruppeDatatjeneste.opprett(spesifikasjon);
        //må relaste kontekst da bruker nå har tilgang til en ny gruppe
        this.kontekstAdministrasjon.relastKontekst();
        return nyGruppe;
    }

    async opprettMedNyAvtale(spesifikasjon: NyAvtaleOgGruppeSpesifikasjon): Promise<DataOgMeta<GruppeModell>> {
        const nyGruppe = await this.gruppeDatatjeneste.opprettMedNyAvtale(spesifikasjon);
        //må relaste kontekst da bruker nå har tilgang til en ny gruppe
        this.kontekstAdministrasjon.relastKontekst();
        return nyGruppe;
    }
}
